'use client'

import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import '@/styles/bauhaus.css'
import { useLanguage } from '@/i18n/LanguageProvider'

export default function BerlinPoetryFestival() {
  const { t, language } = useLanguage()
  const [isLangOpen, setIsLangOpen] = useState(false)
  const langMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setIsLangOpen(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div 
      className="min-h-screen bg-gradient-to-b from-[#1C1C1C] to-[#252525]"
      style={{
        backgroundImage: 'url(/images/bpf-simple-gradient.svg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* 主要内容 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        
        {/* 桥洞诗计划项目介绍 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-20"
        >
          <div className="bg-[#1C1C1C]/30 backdrop-blur-sm border border-[#B9AE9A]/10 rounded-xl overflow-hidden">
            <div className="p-8 md:p-12">
              <div className="space-y-8">
                <h2 className="text-3xl md:text-4xl font-bauhaus text-white mb-8 text-center">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                    {t('qiaodong.title')}
                  </span>
                </h2>
                
                <div className="space-y-6 max-w-5xl mx-auto">
                  <p className="text-white/90 text-lg leading-relaxed">
                    <span className="font-bold text-[#B9AE9A]">{t('qiaodong.title')}</span> {t('qiaodong.intro.paragraph1')}
                  </p>
                  <p className="text-white/80 text-lg leading-relaxed">
                    {t('qiaodong.intro.paragraph2')}
                  </p>
                  <p className="text-white/80 text-lg leading-relaxed">
                    {t('qiaodong.intro.paragraph3')}
                  </p>
                      </div>
                    </div>
                  </div>
                </div>
          </motion.div>

        {/* 功能模块 - Cursor风格 */}
        <div className="space-y-24 mb-20">

          {/* 翻译工作坊 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center"
          >
            <Link to="/Qiaodong/BPF/workshop" className="group block">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bauhaus text-white mb-6 group-hover:text-[#B9AE9A] transition-colors duration-300">
                  {t('bpf.sections.workshop.title')}
                </h2>
                <p className="text-xl text-white/70 leading-relaxed mb-8 group-hover:text-white/90 transition-colors duration-300">
                  {t('bpf.sections.workshop.description')}
                </p>
                
                {/* 演示图片区域 - 翻译工作坊界面 */}
                <div className="bg-[#1C1C1C]/40 backdrop-blur-sm border border-[#B9AE9A]/20 group-hover:border-[#B9AE9A]/40 transition-all duration-300 rounded-2xl overflow-hidden min-h-[350px] relative mb-8">
                  {/* 背景装饰 - 多种颜色组合 */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#60A5FA]/30 via-[#34D399]/25 to-[#FBBF24]/28"></div>
                  <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#FBBF24]/35 via-[#60A5FA]/30 to-transparent"></div>
                  <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-gradient-to-r from-[#60A5FA]/25 via-[#34D399]/20 to-transparent rounded-full blur-3xl"></div>
                  
                  <div className="relative grid grid-cols-1 lg:grid-cols-2 h-full min-h-[350px]">
                    {/* 左侧：工作坊介绍 */}
                    <div className="flex flex-col justify-end items-start p-8 md:p-12">
                      <div className="space-y-6">
                        <h3 className="text-3xl md:text-4xl lg:text-5xl font-bauhaus text-white text-left">
                          <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                            {t('bpf.workshop.workshop_title')}
                          </span>
                        </h3>
                        
                        <p className="text-xl md:text-2xl text-white/80 leading-relaxed max-w-md text-left">
                          {t('bpf.workshop.workshop_description')}
                        </p>
                      </div>
                    </div>
                    
                    {/* 右侧：翻译界面演示 */}
                    <div className="flex items-center justify-center p-8 md:p-12 relative">
                      <div className="relative w-full max-w-md">
                        {/* 翻译作品展示 - 调整为与workshop页面一致的结构 */}
                        <div className="bg-white/95 rounded-lg border border-[#B9AE9A]/80 overflow-hidden backdrop-blur-sm shadow-2xl">
                          {/* 作品标题栏 */}
                          <div className="bg-[#F5F5F5]/98 px-4 py-3 border-b border-[#B9AE9A]/50 flex items-center justify-between">
                            <div className="text-[#8C4233] text-sm font-bauhaus">{t('bpf.workshop.translation_showcase.title')}</div>
                            <div className="text-[#8C4233]/70 text-xs">06/2025</div>
                          </div>
                          
                          {/* 翻译作品内容 - 调整间距与workshop页面一致 */}
                          <div className="p-6 space-y-6">
                            {/* 原文区域 */}
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="text-[#8C4233] text-xs font-mono uppercase tracking-wide">{t('bpf.workshop.translation_showcase.original_label')}</div>
                                <div className="text-[#8C4233]/80 text-xs">{t('bpf.workshop.translation_showcase.author_original')}</div>
                              </div>
                              <div className="bg-[#B9AE9A]/15 rounded-md p-4 border-l-3 border-[#B9AE9A] relative">
                                <p className="text-gray-800 text-sm font-serif leading-relaxed whitespace-pre-line">
                                  {t('bpf.workshop.translation_showcase.poem_original')}
                                </p>
                                <div className="absolute top-2 right-2 w-2 h-2 bg-[#B9AE9A] rounded-full"></div>
                              </div>
                            </div>
                            
                            {/* 翻译分隔线 */}
                            <div className="flex items-center justify-center">
                              <div className="flex items-center space-x-2 text-gray-600">
                                <div className="w-8 h-[1px] bg-gradient-to-r from-transparent to-gray-400"></div>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m0 0l7-7m0 7h18" />
                                </svg>
                                <div className="w-8 h-[1px] bg-gradient-to-r from-gray-400 to-transparent"></div>
                              </div>
                            </div>
                            
                            {/* 译文区域 */}
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="text-[#506B72] text-xs font-mono uppercase tracking-wide">{t('bpf.workshop.translation_showcase.translation_label')}</div>
                                <div className="text-[#506B72]/80 text-xs">{t('bpf.workshop.translation_showcase.author_translation')}</div>
                        </div>
                              <div className="bg-[#506B72]/15 rounded-md p-4 border-l-3 border-[#506B72] relative">
                                <p className="text-gray-800 text-sm font-serif leading-relaxed whitespace-pre-line">
                                  {t('bpf.workshop.translation_showcase.poem_translation')}
                                </p>
                                <div className="absolute top-2 right-2 w-2 h-2 bg-[#506B72] rounded-full"></div>
                        </div>
                      </div>
                      
                            {/* 状态栏 */}
                            <div className="flex items-center justify-between pt-3 border-t border-gray-300">
                              <div className="text-gray-700 text-xs">
                                {t('bpf.workshop.translation_showcase.event_label')}
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 bg-[#27CA3F] rounded-full"></div>
                                <span className="text-[#27CA3F] text-xs">{t('bpf.workshop.translation_showcase.status_completed')}</span>
                        </div>
                        </div>
                      </div>
                    </div>
                    
                        {/* 装饰性光晕 - 调整位置和尺寸与workshop页面一致 */}
                        <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-[#B9AE9A]/20 to-[#8C4233]/20 rounded-full blur-2xl"></div>
                        <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-br from-[#506B72]/20 to-[#B9AE9A]/20 rounded-full blur-2xl"></div>
                        
                        {/* 静态装饰元素 - 调整尺寸和位置 */}
                        <div className="absolute -top-4 right-8 w-8 h-8 bg-[#B9AE9A]/30 rounded-lg backdrop-blur-sm border border-[#B9AE9A]/40 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#B9AE9A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                          </svg>
                        </div>
                        
                        <div className="absolute bottom-8 -left-4 w-6 h-6 bg-[#8C4233]/30 rounded-full backdrop-blur-sm border border-[#8C4233]/40 flex items-center justify-center">
                          <div className="w-2 h-2 bg-[#8C4233] rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              </Link>
            </motion.div>

          {/* 柏林诗歌节活动 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center"
          >
            <Link to="/Qiaodong/BPF/events" className="group block">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bauhaus text-white mb-6 group-hover:text-[#B9AE9A] transition-colors duration-300">
                  {t('bpf.sections.events.title')}
                </h2>
                <p className="text-xl text-white/70 leading-relaxed mb-8 group-hover:text-white/90 transition-colors duration-300">
                  {t('bpf.sections.events.description')}
                </p>
                
                {/* 演示图片区域 - 改为左右分割布局 */}
                <div className="bg-[#1C1C1C]/40 backdrop-blur-sm border border-[#B9AE9A]/20 group-hover:border-[#B9AE9A]/40 transition-all duration-300 rounded-2xl overflow-hidden min-h-[350px] relative mb-8">
                  {/* 背景装饰 - 多种颜色组合 */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#34D399]/30 via-[#FBBF24]/25 to-[#60A5FA]/28"></div>
                  <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#34D399]/35 via-[#FBBF24]/25 to-transparent"></div>
                  <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-gradient-to-r from-[#34D399]/25 via-[#FBBF24]/20 to-transparent rounded-full blur-3xl"></div>
                  
                  <div className="relative grid grid-cols-1 lg:grid-cols-2 h-full min-h-[350px]">
                    {/* 左侧：活动日程界面演示 */}
                    <div className="flex items-center justify-center p-8 md:p-12 relative">
                      <div className="relative w-full max-w-md">
                        {/* 活动日程展示 */}
                        <div className="bg-white/95 rounded-lg border border-[#B9AE9A]/80 overflow-hidden backdrop-blur-sm shadow-2xl">
                          {/* 活动界面顶部 */}
                          <div className="bg-[#F5F5F5]/98 px-4 py-3 border-b border-[#B9AE9A]/50 flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-6 h-6 bg-gradient-to-r from-[#8C4233] to-[#506B72] rounded-md flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <div className="text-[#8C4233] text-sm font-bauhaus">{t('bpf.sections.events.title')} 2025</div>
                            </div>
                            <div className="text-[#8C4233]/70 text-xs">June 2025</div>
                          </div>
                          
                          {/* 活动日程内容 */}
                          <div className="p-6 space-y-4">
                            {/* 6月9日 */}
                            <div className="bg-[#8C4233]/10 rounded-md p-4 border-l-3 border-[#8C4233] relative">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <div className="w-6 h-6 bg-[#8C4233]/20 rounded-md flex items-center justify-center">
                                    <span className="text-[#8C4233] text-xs font-bauhaus">09</span>
                                  </div>
                                  <div className="text-sm text-gray-800 font-bauhaus">{t('events.events.schedule.june_9.title')}</div>
                                </div>
                                <div className="text-xs text-[#8C4233]">19:00</div>
                              </div>
                              <div className="text-xs text-gray-600 ml-8">Literaturhaus Berlin</div>
                              <div className="absolute top-2 right-2 w-2 h-2 bg-[#8C4233] rounded-full"></div>
                            </div>
                            
                            {/* 6月10日 */}
                            <div className="bg-[#506B72]/10 rounded-md p-4 border-l-3 border-[#506B72] relative">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <div className="w-6 h-6 bg-[#506B72]/20 rounded-md flex items-center justify-center">
                                    <span className="text-[#506B72] text-xs font-bauhaus">10</span>
                                  </div>
                                  <div className="text-sm text-gray-800 font-bauhaus">{t('events.events.schedule.june_10.title')}</div>
                                </div>
                                <div className="text-xs text-[#506B72]">14:00</div>
                              </div>
                              <div className="text-xs text-gray-600 ml-8">{t('bpf.sections.workshop.description')}</div>
                              <div className="absolute top-2 right-2 w-2 h-2 bg-[#506B72] rounded-full"></div>
                            </div>
                            
                            {/* 6月12日 */}
                            <div className="bg-[#B9AE9A]/10 rounded-md p-4 border-l-3 border-[#B9AE9A] relative">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <div className="w-6 h-6 bg-[#B9AE9A]/20 rounded-md flex items-center justify-center">
                                    <span className="text-[#B9AE9A] text-xs font-bauhaus">12</span>
                                  </div>
                                  <div className="text-sm text-gray-800 font-bauhaus">{t('events.events.schedule.june_12.title')}</div>
                                </div>
                                <div className="text-xs text-[#B9AE9A]">16:00</div>
                              </div>
                              <div className="text-xs text-gray-600 ml-8">{t('bpf.sections.china_poetry.description')}</div>
                              <div className="absolute top-2 right-2 w-2 h-2 bg-[#B9AE9A] rounded-full"></div>
                            </div>
                          </div>
                          
                          {/* 状态栏 */}
                          <div className="flex items-center justify-between pt-3 px-6 pb-6 border-t border-gray-300">
                            <div className="text-gray-700 text-xs">
                              {t('nav.upcoming_events')}
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="flex -space-x-1">
                                <div className="w-4 h-4 bg-[#B9AE9A]/30 rounded-full border border-white"></div>
                                <div className="w-4 h-4 bg-[#8C4233]/30 rounded-full border border-white"></div>
                                <div className="w-4 h-4 bg-[#506B72]/30 rounded-full border border-white"></div>
                              </div>
                              <span className="text-gray-600 text-xs">{t('bpf.sections.china_poetry.participating_poets')}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* 装饰性光晕 - 与翻译工作坊一致 */}
                        <div className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-[#34D399]/20 to-[#FBBF24]/20 rounded-full blur-2xl"></div>
                        <div className="absolute -bottom-6 -right-6 w-20 h-20 bg-gradient-to-br from-[#60A5FA]/20 to-[#34D399]/20 rounded-full blur-2xl"></div>
                        
                        {/* 静态装饰元素 */}
                        <div className="absolute -top-4 left-8 w-8 h-8 bg-[#34D399]/30 rounded-lg backdrop-blur-sm border border-[#34D399]/40 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#34D399]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                          </svg>
                        </div>
                        
                        <div className="absolute bottom-8 -right-4 w-6 h-6 bg-[#FBBF24]/30 rounded-full backdrop-blur-sm border border-[#FBBF24]/40 flex items-center justify-center">
                          <div className="w-2 h-2 bg-[#FBBF24] rounded-full"></div>
                        </div>
                      </div>
                    </div>
                    
                    {/* 右侧：活动介绍 */}
                    <div className="flex flex-col justify-end items-end p-8 md:p-12">
                      <div className="space-y-6">
                        <h3 className="text-3xl md:text-4xl lg:text-5xl font-bauhaus text-white text-right">
                          <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                            {t('bpf.sections.events.title')}
                          </span>
                        </h3>
                        
                        <p className="text-xl md:text-2xl text-white/80 leading-relaxed max-w-md text-right">
                          {t('bpf.sections.events.description')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>

          {/* 当代中国青年诗人写作生态 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center"
          >
            <Link to="/Qiaodong/BPF/china-poetry" className="group block">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bauhaus text-white mb-6 group-hover:text-[#B9AE9A] transition-colors duration-300">
                  {t('bpf.sections.china_poetry.title')}
                </h2>
                <p className="text-xl text-white/70 leading-relaxed mb-8 group-hover:text-white/90 transition-colors duration-300">
                  {t('bpf.sections.china_poetry.description')}
                </p>
                
                {/* 演示图片区域 - 改为左右分割布局 */}
                <div className="bg-[#1C1C1C]/40 backdrop-blur-sm border border-[#B9AE9A]/20 group-hover:border-[#B9AE9A]/40 transition-all duration-300 rounded-2xl overflow-hidden min-h-[350px] relative mb-8">
                  {/* 背景装饰 - 多种颜色组合 */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#FBBF24]/30 via-[#60A5FA]/25 to-[#34D399]/28"></div>
                  <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#FBBF24]/35 via-[#34D399]/25 to-transparent"></div>
                  <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-gradient-to-r from-[#FBBF24]/25 via-[#60A5FA]/20 to-transparent rounded-full blur-3xl"></div>
                  
                  <div className="relative grid grid-cols-1 lg:grid-cols-2 h-full min-h-[350px]">
                    {/* 左侧：诗歌生态介绍 */}
                    <div className="flex flex-col justify-end items-start p-8 md:p-12">
                      <div className="space-y-6">
                        <h3 className="text-3xl md:text-4xl lg:text-5xl font-bauhaus text-white text-left">
                          <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                            {t('bpf.sections.china_poetry.title')}
                          </span>
                        </h3>
                        
                        <p className="text-xl md:text-2xl text-white/80 leading-relaxed max-w-md text-left">
                          {t('bpf.sections.china_poetry.description')}
                        </p>
                      </div>
                    </div>
                    
                    {/* 右侧：诗歌生态界面展示 */}
                    <div className="flex items-center justify-center p-8 md:p-12 relative">
                      <div className="relative w-full max-w-md">
                        {/* 诗歌生态展示 */}
                        <div className="bg-white/95 rounded-lg border border-[#B9AE9A]/80 overflow-hidden backdrop-blur-sm shadow-2xl">
                          {/* 生态界面顶部 */}
                          <div className="bg-[#F5F5F5]/98 px-4 py-3 border-b border-[#B9AE9A]/50 flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-6 h-6 bg-gradient-to-r from-[#8C4233] to-[#506B72] rounded-md flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                </svg>
                              </div>
                              <div className="text-[#8C4233] text-sm font-bauhaus">{t('bpf.sections.china_poetry.title')}</div>
                            </div>
                            <div className="text-[#8C4233]/70 text-xs">2025 {t('bpf.sections.china_poetry.annual_observation')}</div>
                          </div>
                          
                          {/* 三个主要部分内容 - 2+1布局 */}
                          <div className="p-6 space-y-4">
                            {/* 上排：两个并排的卡片 */}
                            <div className="grid grid-cols-2 gap-3">
                              {/* 生活与洞见诗选展 */}
                              <div className="bg-gradient-to-br from-[#B9AE9A]/10 to-[#B9AE9A]/5 rounded-lg p-3 border border-[#B9AE9A]/30 relative group hover:shadow-md transition-all duration-300">
                                <div className="flex flex-col items-center text-center space-y-2">
                                  <div className="w-10 h-10 bg-[#B9AE9A]/20 rounded-full flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#B9AE9A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                  </div>
                                  <div className="text-xs text-gray-800 font-bauhaus leading-tight">{t('events.china_poetry.collective.title')}</div>
                                  <div className="text-xs text-gray-600 leading-relaxed opacity-80">{t('events.china_poetry.collective.description')}</div>
                                </div>
                                <div className="absolute top-2 right-2 w-1.5 h-1.5 bg-[#B9AE9A] rounded-full group-hover:animate-pulse"></div>
                              </div>
                              
                              {/* 中国摇滚乐与诗歌 */}
                              <div className="bg-gradient-to-br from-[#8C4233]/10 to-[#8C4233]/5 rounded-lg p-3 border border-[#8C4233]/30 relative group hover:shadow-md transition-all duration-300">
                                <div className="flex flex-col items-center text-center space-y-2">
                                  <div className="w-10 h-10 bg-[#8C4233]/20 rounded-full flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#8C4233]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                    </svg>
                                  </div>
                                  <div className="text-xs text-gray-800 font-bauhaus leading-tight">{t('events.china_poetry.rock_music.title')}</div>
                                  <div className="text-xs text-gray-600 leading-relaxed opacity-80">{t('events.china_poetry.rock_music.description')}</div>
                                </div>
                                <div className="absolute top-2 right-2 w-1.5 h-1.5 bg-[#8C4233] rounded-full group-hover:animate-pulse"></div>
                              </div>
                            </div>
                            
                            {/* 下排：一个居中的卡片 */}
                            <div className="flex justify-center">
                              <div className="bg-gradient-to-br from-[#506B72]/10 to-[#506B72]/5 rounded-lg p-4 border border-[#506B72]/30 relative group hover:shadow-md transition-all duration-300 w-full">
                                <div className="flex items-center space-x-3">
                                  <div className="w-12 h-12 bg-[#506B72]/20 rounded-full flex items-center justify-center flex-shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#506B72]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                    </svg>
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm text-gray-800 font-bauhaus mb-1">{t('events.china_poetry.moon_tides.title')}</div>
                                    <div className="text-xs text-gray-600 leading-relaxed">{t('events.china_poetry.moon_tides.description')}</div>
                                  </div>
                                </div>
                                <div className="absolute top-3 right-3 w-2 h-2 bg-[#506B72] rounded-full group-hover:animate-pulse"></div>
                                
                                {/* 特殊装饰：月相图标 */}
                                <div className="absolute -top-1 -right-1 opacity-20">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#506B72]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            
                            {/* 生态连接线装饰 */}
                            <div className="relative mt-2">
                              <svg className="absolute inset-0 w-full h-8 opacity-30" viewBox="0 0 300 32">
                                <defs>
                                  <linearGradient id="ecoLine" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#B9AE9A" stopOpacity="0.4"/>
                                    <stop offset="50%" stopColor="#8C4233" stopOpacity="0.6"/>
                                    <stop offset="100%" stopColor="#506B72" stopOpacity="0.4"/>
                                  </linearGradient>
                                </defs>
                                <path d="M75 12 Q150 8 225 12" stroke="url(#ecoLine)" strokeWidth="1" fill="none" strokeDasharray="3,3" className="animate-pulse"/>
                                <circle cx="75" cy="12" r="1.5" fill="#B9AE9A" opacity="0.6"/>
                                <circle cx="225" cy="12" r="1.5" fill="#8C4233" opacity="0.6"/>
                                <circle cx="150" cy="20" r="2" fill="#506B72" opacity="0.6"/>
                              </svg>
                            </div>
                          </div>
                          
                          {/* 状态栏 */}
                          <div className="flex items-center justify-between pt-3 px-6 pb-6 border-t border-gray-300">
                            <div className="text-gray-700 text-xs">
                              {t('bpf.sections.china_poetry.ecological_observation')}
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="flex -space-x-1">
                                <div className="w-4 h-4 bg-[#B9AE9A]/30 rounded-full border border-white"></div>
                                <div className="w-4 h-4 bg-[#8C4233]/30 rounded-full border border-white"></div>
                                <div className="w-4 h-4 bg-[#506B72]/30 rounded-full border border-white"></div>
                              </div>
                              <span className="text-gray-600 text-xs">{t('bpf.sections.china_poetry.participating_poets')}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* 装饰性光晕 - 与其他模块一致 */}
                        <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-[#FBBF24]/20 to-[#60A5FA]/20 rounded-full blur-2xl"></div>
                        <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-br from-[#34D399]/20 to-[#FBBF24]/20 rounded-full blur-2xl"></div>
                        
                        {/* 静态装饰元素 */}
                        <div className="absolute -top-4 right-8 w-8 h-8 bg-[#FBBF24]/30 rounded-lg backdrop-blur-sm border border-[#FBBF24]/40 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#FBBF24]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                        </div>
                        
                        <div className="absolute bottom-8 -left-4 w-6 h-6 bg-[#60A5FA]/30 rounded-full backdrop-blur-sm border border-[#60A5FA]/40 flex items-center justify-center">
                          <div className="w-2 h-2 bg-[#60A5FA] rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  )
} 